/**
 * Tenant-scoped DB-backed email template editor API.
 *
 * The tenant is derived from the JWT by saas-auth. Slot and locale are the only path
 * parameters Brand Center sends.
 */
import { apiClient, unwrap } from '../client';
import type {
  ApiResponse,
  EmailLocale,
  EmailPublishResult,
  EmailResetResult,
  EmailRevertResult,
  EmailServerPreview,
  EmailSlotCatalog,
  EmailTemplateCell,
  EmailTemplateDetail,
  EmailTemplateDraftBody,
  EmailTemplateName,
  EmailTestSendResult,
} from '../types';

const BASE = '/api/tenant/brand/email-templates';

interface EmailTemplateListEnvelope {
  templates: EmailTemplateCell[];
}

interface EmailTemplateCatalogEnvelope {
  catalog: EmailSlotCatalog[];
}

function cellPath(slot: EmailTemplateName, locale: EmailLocale): string {
  return `${BASE}/${encodeURIComponent(slot)}/${encodeURIComponent(locale)}`;
}

export const emailTemplatesApi = {
  async list(): Promise<EmailTemplateCell[]> {
    const { data } = await apiClient.get<ApiResponse<EmailTemplateListEnvelope>>(BASE);
    return unwrap(data).templates;
  },

  async catalog(): Promise<EmailSlotCatalog[]> {
    const { data } = await apiClient.get<ApiResponse<EmailTemplateCatalogEnvelope>>(
      `${BASE}/catalog`,
    );
    return unwrap(data).catalog;
  },

  async get(slot: EmailTemplateName, locale: EmailLocale): Promise<EmailTemplateDetail> {
    const { data } = await apiClient.get<ApiResponse<EmailTemplateDetail>>(cellPath(slot, locale));
    return unwrap(data);
  },

  async saveDraft(
    slot: EmailTemplateName,
    locale: EmailLocale,
    body: EmailTemplateDraftBody,
  ): Promise<EmailTemplateDetail> {
    const { data } = await apiClient.put<ApiResponse<EmailTemplateDetail>>(
      `${cellPath(slot, locale)}/draft`,
      body,
    );
    return unwrap(data);
  },

  async publish(slot: EmailTemplateName, locale: EmailLocale): Promise<EmailPublishResult> {
    const { data } = await apiClient.post<ApiResponse<EmailPublishResult>>(
      `${cellPath(slot, locale)}/publish`,
    );
    return unwrap(data);
  },

  async revert(slot: EmailTemplateName, locale: EmailLocale): Promise<EmailRevertResult> {
    const { data } = await apiClient.post<ApiResponse<EmailRevertResult>>(
      `${cellPath(slot, locale)}/revert`,
    );
    return unwrap(data);
  },

  async reset(slot: EmailTemplateName, locale: EmailLocale): Promise<EmailResetResult> {
    const { data } = await apiClient.delete<ApiResponse<EmailResetResult>>(cellPath(slot, locale));
    return unwrap(data);
  },

  async preview(slot: EmailTemplateName, locale: EmailLocale): Promise<EmailServerPreview> {
    const { data } = await apiClient.post<ApiResponse<EmailServerPreview>>(
      `${cellPath(slot, locale)}/preview`,
    );
    return unwrap(data);
  },

  async testSend(slot: EmailTemplateName, locale: EmailLocale): Promise<EmailTestSendResult> {
    const { data } = await apiClient.post<ApiResponse<EmailTestSendResult>>(
      `${cellPath(slot, locale)}/test-send`,
    );
    return unwrap(data);
  },
};
